import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Package, MessageCircle, Zap, FlaskConical, Gift,
  Factory, Palette, BadgeCheck, Layers, Scale, Play,
} from "lucide-react";
import SEO from "@/components/site/SEO";
import DraggableMarquee from "@/components/site/DraggableMarquee";
import { api, WA, trackEvent, resolveMediaUrl, getYoutubeEmbedUrl } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useLanguage } from "@/i18n/LanguageContext";
import { IMAGES } from "@/lib/images";
import { bi } from "@/lib/bilingual";
import { useCachedGet } from "@/hooks/useCachedGet";
import { peekCache } from "@/lib/publicCache";

const ADV_ICONS = [Package, ShieldCheck, MessageCircle, Zap, FlaskConical, Gift];
const SVC_ICONS = [Factory, Palette, Layers, BadgeCheck, FlaskConical, Scale];

const HomeLanding = () => {
  const { settings } = useSettings();
  const { t, lang, locale, dict } = useLanguage();
  const h = dict.home;
  const about = dict.about;
  const stats = settings?.stats || {};
  const wa = WA(settings?.whatsapp_number, settings?.whatsapp_template);

  const [heroImage, setHeroImage] = useState("");
  const [aboutVideo, setAboutVideo] = useState({ url: "https://www.youtube.com/watch?v=rzYi5PPHDsk", image: "/mcs-welcome.jpg" });
  const [playingAbout, setPlayingAbout] = useState(false);

  const { data: productsRaw } = useCachedGet("/products", { published: true });
  const { data: articlesRaw } = useCachedGet("/articles", { published: true });
  const products = Array.isArray(productsRaw) ? productsRaw.slice(0, 12) : [];
  const articles = Array.isArray(articlesRaw) ? articlesRaw.slice(0, 3) : [];

  useEffect(() => {
    const home = peekCache("/pages/public-by-route/home");
    const about = peekCache("/pages/public-by-route/about");
    const applyHome = (data) => {
      const hero = (data?.sections || []).find((s) => s.type === "home_hero");
      const side = hero?.props?.sideImage;
      if (side) setHeroImage(resolveMediaUrl(side));
    };
    const applyAbout = (data) => {
      const section = (data?.sections || []).find((s) => (s.props || {}).videoUrl);
      if (section?.props?.videoUrl) {
        setAboutVideo({ url: section.props.videoUrl, image: section.props.image || "/mcs-welcome.jpg" });
      }
    };
    if (home) applyHome(home);
    if (about) applyAbout(about);
    api.get("/pages/public-by-route/home").then(({ data }) => applyHome(data)).catch(() => {});
    api.get("/pages/public-by-route/about").then(({ data }) => applyAbout(data)).catch(() => {});
  }, []);

  const logos = (settings?.client_logos || []).filter((x) => x?.image);
  const certs = (settings?.certifications || []).filter((x) => x?.image);

  const fallbackCats = lang === "en" ? [
    { t: "Collagen Drink", d: "Beauty drinks with collagen and vitamins for the beauty-from-within market.", img: IMAGES.collagen },
    { t: "Fiber Drink", d: "High-fiber formulas for digestion, diet programs, and healthy lifestyles.", img: IMAGES.fiber },
    { t: "Coffee & Cafe", d: "Instant cafe drinks: coffee, cappuccino, latte, and signature blends.", img: IMAGES.coffee },
    { t: "Herbal Drink", d: "Herbs and spices such as ginger, turmeric, and temulawak.", img: IMAGES.herbal },
    { t: "Ice Cream Premix", d: "Hard & soft ice cream premix for cafes, dessert brands, and F&B businesses.", img: IMAGES.chocolate },
    { t: "Milkshake", d: "Milkshake powder drinks with custom flavors for retail and HoReCa.", img: IMAGES.tea },
  ] : [
    { t: "Minuman Kolagen", d: "Minuman kecantikan dengan kolagen dan vitamin untuk tren beauty from within.", img: IMAGES.collagen },
    { t: "Minuman Fiber", d: "Formula tinggi serat untuk pencernaan, diet, dan gaya hidup sehat.", img: IMAGES.fiber },
    { t: "Kopi & Cafe", d: "Minuman cafe instan: kopi, cappuccino, latte, dan racikan khas brand Anda.", img: IMAGES.coffee },
    { t: "Minuman Herbal", d: "Bahan herbal dan rempah seperti jahe, kunyit, dan temulawak.", img: IMAGES.herbal },
    { t: "Premix Ice Cream", d: "Premix ice cream hard & soft untuk cafe, dessert brand, dan bisnis F&B.", img: IMAGES.chocolate },
    { t: "Milkshake", d: "Minuman serbuk milkshake dengan rasa custom untuk ritel dan HoReCa.", img: IMAGES.tea },
  ];

  const showProducts = products.length
    ? products.slice(0, 6).map((p) => ({
      t: bi(p.name, lang),
      d: bi(p.description, lang) || "",
      img: resolveMediaUrl(p.image) || IMAGES.lab,
      to: `/produk/${p.slug}`,
    }))
    : fallbackCats.map((p) => ({ ...p, to: "/produk" }));

  const num = (v, fallback) => String(v || fallback).replace("+", "");
  const aboutEmbed = getYoutubeEmbedUrl(aboutVideo.url);
  const aboutThumb = resolveMediaUrl(aboutVideo.image) || "/mcs-welcome.jpg";

  return (
    <div>
      <SEO title={t("home.seoTitle")} description={t("home.seoDesc")} />

      <section className="sas-hero" data-testid="hero-section">
        <div className="sas-wrap sas-hero-grid">
          <div>
            <div className="sas-kicker">{h.badge}</div>
            <h1>
              {h.titleBefore}<em>{h.titleHighlight}</em>{h.titleAfter}
            </h1>
            <h2>{h.heroSubtitle}</h2>
            <p>{h.lead}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href={wa} target="_blank" rel="noopener noreferrer" className="sas-btn" onClick={() => trackEvent("click", "/", "whatsapp")}>
                {t("nav.consult")}
              </a>
              <Link to="/produk" className="sas-btn sas-btn-ghost">{h.ctaCatalog}</Link>
            </div>
          </div>
          <img
            src={heroImage || "/hero-products-clean.png"}
            alt="Produk maklon Mandala"
            className="sas-hero-img"
            onError={(e) => {
              if (e.currentTarget.dataset.fallback === "1") return;
              e.currentTarget.dataset.fallback = "1";
              e.currentTarget.src = "/hero-products-clean.png";
            }}
          />
        </div>
      </section>

      <section className="sas-about" id="tentang">
        <div className="sas-wrap sas-about-grid">
          {aboutEmbed ? (
            <div className="sas-about-video">
              {playingAbout ? (
                <iframe
                  src={`${aboutEmbed}${aboutEmbed.includes("?") ? "&" : "?"}autoplay=1`}
                  title={about.imgAlt}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                  referrerPolicy="strict-origin-when-cross-origin"
                />
              ) : (
                <button type="button" onClick={() => setPlayingAbout(true)} aria-label="Putar video">
                  <img src={aboutThumb} alt={about.imgAlt} />
                  <span className="absolute inset-0 bg-black/35" />
                  <span className="absolute inset-0 flex items-center justify-center">
                    <span className="relative flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20">
                      <span className="absolute inset-0 rounded-full bg-white/45 video-play-ping" />
                      <span className="absolute inset-0 rounded-full bg-white/25 video-play-ping video-play-ping-delay" />
                      <span className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white text-maroon-800 flex items-center justify-center shadow-xl">
                        <Play size={28} className="ml-1" fill="currentColor" />
                      </span>
                    </span>
                  </span>
                </button>
              )}
            </div>
          ) : (
            <img src={aboutThumb} alt={about.imgAlt} className="sas-about-photo" />
          )}
          <div>
            <div className="sas-kicker">{about.overline}</div>
            <h2 className="sas-title">{about.storyTitle}</h2>
            <p className="sas-lead">{about.storyP1}</p>
            <p className="sas-lead mt-3">{about.storyP2}</p>
            <div className="mt-6">
              <Link to="/tentang" className="sas-btn sas-btn-ghost">{h.whyCta}</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="sas-process">
        <div className="sas-wrap">
          <div className="text-center max-w-3xl mx-auto">
            <div className="sas-kicker">{h.processOverline}</div>
            <h2 className="sas-title">{h.processTitle}</h2>
            <p className="sas-lead">{h.processSubtitle}</p>
          </div>
          <ul className="sas-pains mt-8" style={{ maxWidth: 840, marginLeft: "auto", marginRight: "auto" }}>
            {(h.process || []).map((item, i) => (
              <li key={item.t}>
                <span className="dot">{i + 1}</span>
                <div>
                  <strong>{item.t}</strong>
                  <div style={{ fontWeight: 500, color: "#5c6d7a" }}>{item.d}</div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="sas-reasons">
        <div className="sas-wrap">
          <div className="text-center max-w-3xl mx-auto">
            <div className="sas-kicker">{h.whyOverline}</div>
            <h2 className="sas-title">{h.whyTitleBefore}{h.whyTitleHighlight}{h.whyTitleAfter}</h2>
            <p className="sas-lead">{h.whyBody}</p>
          </div>
          <div className="sas-reason-grid cols-3">
            {(h.advantages || []).map((r, i) => {
              const Ico = ADV_ICONS[i] || ShieldCheck;
              return (
                <article key={r.title} className="sas-reason">
                  <div className="ico"><Ico size={28} /></div>
                  <h3>{r.title}</h3>
                  <p>{r.desc}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="sas-stats">
        <div className="sas-wrap sas-stats-grid">
          <div className="sas-stat"><strong>{num(stats.brands_served, "500")}+</strong><span>{h.statBrands}</span></div>
          <div className="sas-stat"><strong>{num(stats.formulas_created, "1000")}+</strong><span>{h.statFormulas}</span></div>
          <div className="sas-stat"><strong>{num(stats.products_launched, "300")}+</strong><span>{h.statProducts}</span></div>
          <div className="sas-stat"><strong>{new Date().getFullYear() - 2008}Th</strong><span>{h.statSince}</span></div>
        </div>
      </section>

      {certs.length > 0 && (
        <section className="sas-sec" id="sertifikat">
          <div className="sas-wrap text-center">
            <div className="sas-kicker">{about.certOverline}</div>
            <h2 className="sas-title">{about.certTitle}</h2>
            <p className="sas-lead max-w-2xl mx-auto">{about.certSubtitle}</p>
          </div>
          <div className="mt-8">
            <DraggableMarquee gradient gradientColor="#ffffff" speed={26}>
              {certs.map((c) => (
                <div key={c.id} className="sas-cert">
                  <img src={resolveMediaUrl(c.image)} alt={c.name || "Sertifikat"} draggable={false} />
                </div>
              ))}
            </DraggableMarquee>
          </div>
        </section>
      )}

      {logos.length > 0 && (
        <section className="sas-sec alt">
          <div className="sas-wrap text-center">
            <div className="sas-kicker">{h.testimonialsOverline}</div>
            <h2 className="sas-title">{h.testimonialsTitle}</h2>
          </div>
          <div className="mt-8">
            <DraggableMarquee gradient gradientColor="#fff5f5" speed={32}>
              {logos.map((logo) => (
                <div key={logo.id} className="sas-logo">
                  <img src={resolveMediaUrl(logo.image)} alt={logo.name || "Klien"} draggable={false} />
                </div>
              ))}
            </DraggableMarquee>
          </div>
        </section>
      )}

      <section className="sas-sec">
        <div className="sas-wrap">
          <div className="text-center max-w-3xl mx-auto">
            <div className="sas-kicker">{h.servicesOverline}</div>
            <h2 className="sas-title">{h.servicesTitle}</h2>
            <p className="sas-lead">{h.servicesSubtitle}</p>
          </div>
          <div className="sas-svc-grid cols-3">
            {(h.services || []).map((s, i) => {
              const Ico = SVC_ICONS[i] || Factory;
              return (
                <Link key={s.title} to="/layanan" className="sas-svc">
                  <div className="ico"><Ico size={22} /></div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <section className="sas-sec alt">
        <div className="sas-wrap">
          <div className="text-center max-w-3xl mx-auto">
            <div className="sas-kicker">{h.productsOverline}</div>
            <h2 className="sas-title">{h.productsTitle}</h2>
            <p className="sas-lead">{h.productsSubtitle}</p>
          </div>
          <div className="sas-prod-grid">
            {showProducts.map((p) => (
              <Link key={p.t} to={p.to} className="sas-prod">
                <div className="sas-prod-media">
                  <img src={p.img} alt={p.t} />
                </div>
                <div className="sas-prod-body">
                  <h3>{p.t}</h3>
                  <p>{p.d}</p>
                  <span className="sas-btn">{t("common.learnMore")}</span>
                </div>
              </Link>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/produk" className="sas-btn">{h.seeAllProducts}</Link>
          </div>
        </div>
      </section>

      {articles.length > 0 && (
        <section className="sas-sec alt">
          <div className="sas-wrap">
            <div className="flex items-end justify-between gap-4 flex-wrap">
              <div>
                <div className="sas-kicker">{t("articles.overline")}</div>
                <h2 className="sas-title">{t("articles.heroTitle")}</h2>
                <p className="sas-lead">{t("articles.heroLead")}</p>
              </div>
              <Link to="/artikel" className="sas-btn sas-btn-ghost">{t("articles.readMore")}</Link>
            </div>
            <div className="sas-art-grid">
              {articles.map((a) => (
                <Link key={a.id} to={`/artikel/${a.slug}`} className="sas-art">
                  {a.thumbnail ? <img src={resolveMediaUrl(a.thumbnail)} alt={bi(a.title, lang)} /> : <img src={IMAGES.lab} alt="" />}
                  <div className="sas-art-body">
                    <time>{a.created_at ? new Date(a.created_at).toLocaleDateString(locale || "id-ID", { year: "numeric", month: "long", day: "numeric" }) : ""}</time>
                    <h3>{bi(a.title, lang)}</h3>
                    <p>{bi(a.excerpt, lang) || ""}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="sas-cta">
        <div className="sas-wrap">
          <div className="sas-kicker" style={{ color: "rgba(255,255,255,0.75)" }}>{h.ctaOverline}</div>
          <h2>{h.ctaTitle}</h2>
          <p>{h.ctaBody}</p>
          <a href={wa} target="_blank" rel="noopener noreferrer" className="sas-btn" onClick={() => trackEvent("click", "/", "whatsapp")}>
            {t("common.consultWa")}
          </a>
        </div>
      </section>
    </div>
  );
};

export default HomeLanding;
